import { defineFontResolver, type FontSource, type FontSourceSubstitution, type MarkedFontResolver } from "@docx-editor.dev/core";
import { loadDefaultFonts } from "@docx-editor.dev/fonts";
import robotoRegular from "@expo-google-fonts/roboto/400Regular/Roboto_400Regular.ttf?url";
import robotoItalic from "@expo-google-fonts/roboto/400Regular_Italic/Roboto_400Regular_Italic.ttf?url";
import robotoBold from "@expo-google-fonts/roboto/700Bold/Roboto_700Bold.ttf?url";
import robotoBoldItalic from "@expo-google-fonts/roboto/700Bold_Italic/Roboto_700Bold_Italic.ttf?url";
import { DEFAULT_FAMILY, DEFAULT_SIZE, type Hosted, isPackaged, normalise, packagedFamilyOf, type Reading, reading } from "./substitutes";

// The four faces a run can ask for, a run being bold or not and italic or not and nothing in between.
const FACES = [
  { weight: 400, style: "normal" },
  { weight: 700, style: "normal" },
  { weight: 400, style: "italic" },
  { weight: 700, style: "italic" },
] as const;

// Static instances rather than the variable face the site is set in: the shaper refuses variation axes, and a
// variable file would be measured at one weight and drawn bold at another.
const ROBOTO = [
  { ...FACES[0], url: robotoRegular },
  { ...FACES[1], url: robotoBold },
  { ...FACES[2], url: robotoItalic },
  { ...FACES[3], url: robotoBoldItalic },
];

// Asked once per document, after it is read and with the families it names, so a file set in Times fetches Liberation
// Serif and Roboto and never the 2.7 MB of Carlito it has no use for.
export function documentFonts(onRead: (readings: Reading[]) => void): MarkedFontResolver {
  return defineFontResolver(async ({ families, signal }) => {
    const readings = distinct(families).map(reading);
    onRead(readings);

    // The default is always wanted, since it is what every run naming no family at all is measured in.
    const hosted = new Set<Hosted>([DEFAULT_FAMILY]);
    for (const { face } of readings) if (face) hosted.add(face);
    const loaded = await loadHosted([...hosted], signal);

    const substitutions: FontSourceSubstitution[] = [];
    for (const { family, face, kind } of readings) {
      if (!face || kind === "own") continue;
      for (const { weight, style } of FACES) {
        // Word's six are the fonts package's own redirects, which carry the line metrics Century Gothic needs.
        const packaged = isPackaged(family)
          && loaded.substitutions.find(({ from }) =>
            normalise(from.family) === normalise(family) && from.weight === weight && from.style === style
          );
        substitutions.push(
          packaged || { from: { family, weight, style }, to: { family: face, weight, style } },
        );
      }
    }

    return {
      sources: loaded.sources,
      substitutions,
      defaultFont: { family: DEFAULT_FAMILY, sizeHalfPoints: DEFAULT_SIZE },
    };
  });
}

// The bytes the stand-ins are read from: the fonts package for its six, and this site's own copy for Roboto.
async function loadHosted(hosted: Hosted[], signal?: AbortSignal) {
  const families = hosted.map(packagedFamilyOf).filter((family) => family !== null);
  const [packaged, roboto] = await Promise.all([
    families.length > 0 ? loadDefaultFonts({ families, ...(signal ? { signal } : {}) }) : null,
    hosted.includes("Roboto") ? loadRoboto(signal) : [],
  ]);
  return {
    sources: [...(packaged?.sources ?? []), ...roboto],
    substitutions: packaged?.substitutions ?? [],
  };
}

// A face that will not load is left out rather than failing the rest, and its family falls to the engine's own
// measurement; only somebody opening another document in the meantime is a reason to stop.
async function loadRoboto(signal?: AbortSignal): Promise<FontSource[]> {
  const faces = await Promise.all(ROBOTO.map(async ({ weight, style, url }) => {
    try {
      const response = await fetch(url, signal ? { signal } : {});
      if (!response.ok) return null;
      const bytes = new Uint8Array(await response.arrayBuffer());
      return {
        request: { family: DEFAULT_FAMILY, weight, style },
        id: `roboto:${weight}:${style}`,
        bytes,
        hash: await sha256(bytes),
        faceIndex: 0,
      } satisfies FontSource;
    } catch (error) {
      if (signal?.aborted) throw signal.reason ?? error;
      return null;
    }
  }));
  return faces.filter((face) => face !== null);
}

function distinct(families: readonly string[]): string[] {
  const seen = new Set<string>();
  return families.filter((family) => {
    const key = normalise(family);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// The engine re-derives the digest of every face it is handed and refuses one that does not match what it was told.
async function sha256(bytes: Uint8Array): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes as BufferSource));
  return `sha256:${Array.from(digest, (byte) => byte.toString(16).padStart(2, "0")).join("")}`;
}
